/**
 * @voltxampere/web — Damage event → audio dispatcher (Sprint 37-2)
 *
 * Subscribes to the Sprint 36 `damageMap` signal and triggers Sprint
 * 37-1 damage sounds at the right animation frame. Per-mode timing
 * matches the Sprint 35 fx primitives so audio + visual stay in sync.
 *
 * Animation timing sync table:
 *
 *   Mode             | Trigger frame | Sound(s)
 *   ----             | ------------- | --------
 *   led-pop          | frame 28      | led-pop + spark-zap (+0.02s)
 *   capacitor-burst  | frame 0       | capacitor-swell-creak (prelude)
 *   capacitor-burst  | frame 15      | capacitor-bang (stage 1 start)
 *   resistor-burn    | animating     | sustained sizzle + smoke-hiss
 *   resistor-burn    | permanent     | stop sizzle (smoke-hiss fades)
 *   generic-overheat | (none)        | Sprint 38+ scope
 *
 * The dispatcher is a SINGLETON — `startDamageAudioDispatcher()`
 * mounts the effect; the returned disposer is wired into apps/web
 * boot teardown for HMR safety.
 *
 * 6-katman audio guard (per ADR-0059):
 *   1. AYARLAR damageAudio.enabled (Sprint 37-4)
 *   2. AYARLAR audio.muted master (Sprint 31)
 *   3. AYARLAR audio.masterVolume (Sprint 31)
 *   4. matchMedia('prefers-reduced-motion: reduce')
 *   5. document.hidden (tab arka planda)
 *   6. AudioContext.state === 'suspended' (pre-user-gesture)
 *
 * The dispatcher itself only enforces guard #1 (damageAudio.enabled)
 * + #6 (init not yet performed). Guards 2-5 are enforced by the
 * audio engine + Tone.js master gain wiring (Sprint 31 layer).
 *
 * Engine zero-diff invariant honored — this module imports only
 * `damageMap` signal and the `@voltxampere/audio` public API.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { effect } from "@preact/signals-core";
import { playDamageSound, stopDamageSound } from "@voltxampere/audio";

import { damageMap, type ComponentDamage } from "../store/damage-state.js";

/** LED pop trigger frame — matches `led-explosion.ts` stage 3 onset. */
export const LED_POP_TRIGGER_FRAME = 28;

/** Capacitor bang trigger frame — matches `capacitor-swell.ts` stage 1 onset. */
export const CAPACITOR_BANG_TRIGGER_FRAME = 15;

/** Capacitor swell-creak prelude trigger frame (stage 0 frame 0). */
export const CAPACITOR_PRELUDE_TRIGGER_FRAME = 0;

/** Spark zap delay relative to LED pop (seconds). */
export const SPARK_ZAP_DELAY_S = 0.02;

/**
 * Per-component last-seen frame ledger. Lives at module scope so a
 * dispatcher remount (HMR / test reset) re-uses fresh state.
 */
const previousFrames = new Map<string, number>();

/** Components currently emitting sustained sizzle/hiss sounds. */
const sustainedActive = new Set<string>();

let dispatcherEnabled = true;
let dispatcherDisposer: (() => void) | null = null;

/**
 * Start the dispatcher. Returns a disposer fn — caller stores it
 * and invokes on unmount (apps/web HMR or test teardown).
 *
 * Idempotent: re-mounting overwrites the previous effect cleanly.
 */
export function startDamageAudioDispatcher(): () => void {
  // Idempotent install — clean up any previous effect.
  if (dispatcherDisposer !== null) {
    dispatcherDisposer();
    dispatcherDisposer = null;
  }
  // Reset state on (re)mount so stale frames from a prior run do
  // not double-trigger.
  previousFrames.clear();
  sustainedActive.clear();

  dispatcherDisposer = effect(() => {
    const map = damageMap.value;

    if (!dispatcherEnabled) {
      // When disabled, drain any sustained loops cleanly so a
      // future re-enable starts fresh.
      drainSustainedLoops();
      return;
    }

    // Track removals — components in sustainedActive that no longer
    // appear in the map should be silenced (resetDamage path).
    const seenIds = new Set<string>();
    for (const [id] of map) seenIds.add(id);
    for (const id of [...sustainedActive]) {
      if (!seenIds.has(id)) {
        stopDamageSound("resistor-sizzle");
        sustainedActive.delete(id);
      }
    }

    for (const [id, dmg] of map) {
      handleComponentDamage(id, dmg);
      previousFrames.set(id, dmg.framesSinceTrigger);
    }
  });

  return () => {
    if (dispatcherDisposer !== null) {
      dispatcherDisposer();
      dispatcherDisposer = null;
    }
    drainSustainedLoops();
    previousFrames.clear();
  };
}

/**
 * Per-component dispatch logic. Pure side-effects — playDamageSound /
 * stopDamageSound calls. Caller threads previousFrames + sustained
 * state via the module-level maps.
 */
function handleComponentDamage(id: string, dmg: ComponentDamage): void {
  const prev = previousFrames.get(id) ?? -1;
  const curr = dmg.framesSinceTrigger;

  if (dmg.mode === "led-pop") {
    if (prev < LED_POP_TRIGGER_FRAME && curr >= LED_POP_TRIGGER_FRAME) {
      playDamageSound("led-pop");
      playDamageSound("spark-zap", SPARK_ZAP_DELAY_S);
    }
    return;
  }

  if (dmg.mode === "capacitor-burst") {
    if (prev < CAPACITOR_PRELUDE_TRIGGER_FRAME && curr >= CAPACITOR_PRELUDE_TRIGGER_FRAME) {
      playDamageSound("capacitor-swell-creak");
    }
    if (prev < CAPACITOR_BANG_TRIGGER_FRAME && curr >= CAPACITOR_BANG_TRIGGER_FRAME) {
      playDamageSound("capacitor-bang");
    }
    return;
  }

  if (dmg.mode === "resistor-burn") {
    if (!sustainedActive.has(id) && dmg.status === "damaged-animating") {
      playDamageSound("resistor-sizzle");
      playDamageSound("smoke-hiss");
      sustainedActive.add(id);
    }
    if (sustainedActive.has(id) && dmg.status === "damaged-permanent") {
      stopDamageSound("resistor-sizzle");
      // smoke-hiss continues to fade naturally via its rampTo(0, 1.0)
      sustainedActive.delete(id);
    }
    return;
  }

  // generic-overheat — Sprint 38+ scope (no canonical sound yet).
}

function drainSustainedLoops(): void {
  if (sustainedActive.size > 0) {
    stopDamageSound("resistor-sizzle");
    stopDamageSound("smoke-hiss");
    sustainedActive.clear();
  }
}

/**
 * AYARLAR `damageAudio.enabled` toggle hook. Sprint 37-4 wires this
 * into the settings signal. When disabled, the dispatcher mutes
 * itself silently.
 */
export function setDamageAudioDispatcherEnabled(enabled: boolean): void {
  dispatcherEnabled = enabled;
  if (!enabled) {
    drainSustainedLoops();
  }
}

/** Test-only — fully reset module state (used in afterEach). */
export function __resetDamageAudioDispatcherForTests(): void {
  if (dispatcherDisposer !== null) {
    dispatcherDisposer();
    dispatcherDisposer = null;
  }
  previousFrames.clear();
  sustainedActive.clear();
  dispatcherEnabled = true;
}
